/**
 * Assembles the relationship graph behind a single vendor payment.
 *
 * Kept out of the payment data client because the two have different shapes:
 * the client reads and writes one row, while this fans out across three tables
 * to answer "what does this payment settle, and what else touched it".
 */

import type {
  VendorPayment,
  VendorPaymentPurchaseOrderSummary,
  VendorPaymentRelated,
  VendorPaymentRemainderCredit,
  VendorPaymentSibling,
} from "@/features/vendor-payments/types";
import { getBackendClient } from "@/lib/backend";
import { cast } from "@/lib/utils/query-helpers";

const backend = getBackendClient();

const RELATED_SIBLING_LIMIT = 20;
const RELATED_CREDIT_LIMIT = 10;

async function getPurchaseOrderSummary(
  purchaseOrderId: string | null,
): Promise<VendorPaymentPurchaseOrderSummary | null> {
  if (!purchaseOrderId) {
    return null;
  }

  const { data, error } = await backend.data
    .from("gl_purchase_orders")
    .select("id, uid, total_amount, total_paid, balance, payment_status")
    .eq("id", purchaseOrderId)
    .maybeSingle();

  if (error) {
    throw new Error(error.message);
  }

  return data ? cast<VendorPaymentPurchaseOrderSummary>(data) : null;
}

async function getSiblingPayments(
  purchaseOrderId: string | null,
  paymentId: string,
): Promise<VendorPaymentSibling[]> {
  if (!purchaseOrderId) {
    return [];
  }

  const { data, error } = await backend.data
    .from("gl_vendor_payments")
    .select("id, uid, amount, date, method")
    .eq("purchase_order_id", purchaseOrderId)
    .neq("id", paymentId)
    .order("date", { ascending: false })
    .limit(RELATED_SIBLING_LIMIT);

  if (error) {
    throw new Error(error.message);
  }

  return cast<VendorPaymentSibling[]>(data || []);
}

/**
 * Vendor credit lineage is a real FK:
 * `gl_vendor_credits.source_vendor_payment_id` is stamped by
 * `xan_apply_split_payment_core`, so the remainder credit this payment produced
 * is identified exactly rather than heuristically. A legacy payment predating
 * the column matches no credit.
 */
async function getRemainderCredits(paymentId: string): Promise<VendorPaymentRemainderCredit[]> {
  const { data, error } = await backend.data
    .from("gl_vendor_credits")
    .select("id, uid, amount, date")
    .eq("source_vendor_payment_id", paymentId)
    .order("date", { ascending: false })
    .limit(RELATED_CREDIT_LIMIT);

  if (error) {
    throw new Error(error.message);
  }

  return cast<VendorPaymentRemainderCredit[]>(data || []);
}

/**
 * Resolves everything the payment detail surface links out to. Kept separate
 * from the payment query so a relationship failure still leaves the payment's
 * own fields on screen.
 *
 * Takes the loaded payment rather than an id: every caller already holds the
 * row, and re-reading it here would be a second fetch of the same record under
 * a key TanStack Query cannot dedupe against the first.
 */
export async function getVendorPaymentRelated(
  payment: VendorPayment,
): Promise<VendorPaymentRelated> {
  const [purchaseOrder, siblingPayments, remainderCredits] = await Promise.all([
    getPurchaseOrderSummary(payment.purchase_order_id),
    getSiblingPayments(payment.purchase_order_id, payment.id),
    getRemainderCredits(payment.id),
  ]);

  return {
    account: payment.gl_accounts ?? null,
    purchaseOrder,
    remainderCredits,
    siblingPayments,
  };
}
